/**
 * Helpers to surface backend validation errors on Formik forms.
 *
 * The v4 function-app validators reply 400 with a JSON array of
 * `{ field, message }` objects. Depending on the call path the FE receives it as:
 *  - a parsed array (axios parsed a JSON response body),
 *  - a raw JSON string (function host replies text/plain),
 *  - `{ Failed: "<json array string>" }` (container-app proxy wrapper),
 *  - `{ detail: [{ loc, msg }] }` (FastAPI request-model validation).
 */

export interface ApiFieldError {
  field: string;
  message: string;
}

const parseMaybeJson = (value: any): any => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const toFieldErrors = (candidate: any): ApiFieldError[] => {
  if (!Array.isArray(candidate)) return [];
  const errors: ApiFieldError[] = [];
  candidate.forEach((item: any) => {
    if (!item) return;
    if (typeof item === "string") {
      errors.push({ field: "", message: item });
      return;
    }
    if (item.field !== undefined || item.message !== undefined) {
      errors.push({
        field: String(item.field ?? ""),
        message: String(item.message ?? "Validation error"),
      });
      return;
    }
    // FastAPI detail entries: { loc: ["body", "tcNo"], msg: "..." }
    if (item.msg !== undefined) {
      const loc = Array.isArray(item.loc) ? item.loc[item.loc.length - 1] : "";
      // Pydantic prefixes raised ValueErrors with "Value error, " — strip the
      // technical prefix so users see only the actual validation message.
      const message = String(item.msg).replace(
        /^(Value error|Assertion failed),\s*/i,
        ""
      );
      errors.push({ field: String(loc ?? ""), message });
    }
  });
  return errors;
};

export const extractApiFieldErrors = (data: any): ApiFieldError[] => {
  const parsed = parseMaybeJson(data);
  if (Array.isArray(parsed)) return toFieldErrors(parsed);
  if (parsed && typeof parsed === "object") {
    const failed = parseMaybeJson(parsed.Failed);
    if (Array.isArray(failed)) return toFieldErrors(failed);
    // The TC submit failure arrives as an OBJECT under Failed
    // ({errors: [...], details: [{field, message}]}) via the container-app
    // wrapper — surface its field details so callers suppress the raw
    // "Final TC v4 JSON validation failed" toast and show only the
    // translated per-field messages (86ajcn0z8).
    if (failed && typeof failed === "object") {
      if (Array.isArray(failed.details)) return toFieldErrors(failed.details);
      if (Array.isArray(failed.detail)) return toFieldErrors(failed.detail);
      if (Array.isArray(failed.errors)) return toFieldErrors(failed.errors);
    }
    if (Array.isArray(parsed.errors)) return toFieldErrors(parsed.errors);
    if (Array.isArray(parsed.detail)) return toFieldErrors(parsed.detail);
    if (Array.isArray(parsed.details)) return toFieldErrors(parsed.details);
  }
  return [];
};

/**
 * Marks the matching Formik fields as touched + errored so they highlight inline.
 * Returns the errors that could NOT be mapped to a visible form field (callers
 * usually toast those instead so the message is not silently dropped).
 */
export const applyApiFieldErrorsToFormik = (
  fieldErrors: ApiFieldError[],
  formik: any,
  aliasMap: Record<string, string> = {}
): ApiFieldError[] => {
  const unmapped: ApiFieldError[] = [];
  if (!formik) return fieldErrors;
  const formValues = formik.values || {};
  fieldErrors.forEach((fieldError) => {
    const target = aliasMap[fieldError.field] ?? fieldError.field;
    if (
      target &&
      Object.prototype.hasOwnProperty.call(formValues, target)
    ) {
      formik.setFieldTouched(target, true, false);
      formik.setFieldError(target, fieldError.message);
    } else {
      unmapped.push(fieldError);
    }
  });
  return unmapped;
};

/**
 * Scrolls the first errored input into view (same selector strategy as
 * useScrollToError, usable outside a Formik render cycle).
 */
export const scrollToFirstFormError = () => {
  setTimeout(() => {
    const errorElement = document.querySelector(
      '.Mui-error, .tab-warning, [aria-invalid="true"]'
    );
    errorElement?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 100);
};
